import {Fn, Resource} from '@raison/types';
import {hashKey} from '@tanstack/react-query';
import {useMemo} from 'react';
import {useDispatch, useSelector, useStore} from 'react-redux';
import {Store} from 'redux';
import {createTracedSelector} from './useTracedSelector';

interface AppStore extends Omit<Store<AppModel>, 'dispatch'> {
    dispatch: AppDispatch;
}
export const useAppDispatch = useDispatch.withTypes<AppDispatch>();
export const useAppSelector = useSelector.withTypes<AppModel>();
export const useAppStore = useStore.withTypes<AppStore>();

export function createUseResourceSuspense<T, A extends any[]>(selector: (state: AppModel, ...args: A) => Resource<T>) {
    const cache = createUseResourceSuspense.cache;

    return (...args: A) => {
        const store = useAppStore();

        const traced = useMemo(() => createTracedSelector(selector, ...args), [...args]);

        const resource = useAppSelector(traced);

        if (resource.isSuccess) {
            return resource.data;
        }

        if (resource.isError) {
            throw resource.error;
        }

        const key = hashKey(args);
        const map = cache.get(selector) ?? cache.set(selector, new Map()).get(selector)!;
        const pending = map.get(key);

        if (pending) {
            throw pending;
        }

        const promise = new Promise<T>((resolve, reject) => {
            const unsub = store.subscribe(() => {
                const resource = traced(store.getState());

                if (resource.isSuccess) {
                    unsub();
                    resolve(resource.data);
                } else if (resource.isError) {
                    unsub();
                    reject(resource.error);
                }
            });
        }).finally(() => {
            map.delete(key);
            if (map.size === 0) {
                cache.delete(selector);
            }
        });

        map.set(key, promise);

        throw promise;
    };
}
createUseResourceSuspense.cache = new WeakMap<Fn.Any, Map<string, Promise<unknown>>>();
